import * as THREE from 'three';

/**
 * Low-poly agricultural pickup.
 * Local length along Z, front toward -Z (nose at negative Z).
 */
export function createPickupTruck(colorHex = 0xd94e34): THREE.Group {
  const truck = new THREE.Group();

  const bodyMat = new THREE.MeshStandardMaterial({
    color: colorHex,
    flatShading: true,
    roughness: 0.6,
  });
  const darkBodyMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(colorHex).multiplyScalar(0.72),
    flatShading: true,
    roughness: 0.65,
  });
  const chassisMat = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    flatShading: true,
    roughness: 0.9,
  });
  const bedMat = new THREE.MeshStandardMaterial({
    color: 0x2c2c2c,
    flatShading: true,
    roughness: 0.9,
  });
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x87a0b8,
    flatShading: true,
    roughness: 0.25,
    metalness: 0.15,
  });
  const tireMat = new THREE.MeshStandardMaterial({
    color: 0x151515,
    flatShading: true,
    roughness: 0.95,
  });
  const rimMat = new THREE.MeshStandardMaterial({
    color: 0x6b7280,
    flatShading: true,
    roughness: 0.45,
    metalness: 0.4,
  });
  const lightMat = new THREE.MeshStandardMaterial({
    color: 0xfff3c4,
    emissive: 0xffe08a,
    emissiveIntensity: 0.35,
    roughness: 0.4,
  });
  const tailLightMat = new THREE.MeshStandardMaterial({
    color: 0xb91c1c,
    emissive: 0x7f1d1d,
    emissiveIntensity: 0.25,
    roughness: 0.5,
  });

  const add = (
    geo: THREE.BufferGeometry,
    mat: THREE.Material,
    x: number,
    y: number,
    z: number,
  ): THREE.Mesh => {
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    truck.add(mesh);
    return mesh;
  };

  // --- Chassis ---
  add(new THREE.BoxGeometry(1.55, 0.18, 3.2), chassisMat, 0, 0.28, 0.05);

  // --- Front bumper ---
  add(new THREE.BoxGeometry(1.7, 0.22, 0.22), chassisMat, 0, 0.32, -1.62);

  // --- Hood / nose (lower than cabin) ---
  add(new THREE.BoxGeometry(1.55, 0.42, 0.95), bodyMat, 0, 0.58, -1.05);
  // Slight hood scoop / center ridge
  add(new THREE.BoxGeometry(0.55, 0.08, 0.7), darkBodyMat, 0, 0.82, -1.05);

  // --- Cabin lower body ---
  add(new THREE.BoxGeometry(1.6, 0.5, 1.15), bodyMat, 0, 0.62, -0.05);

  // --- Cabin upper (greenhouse) ---
  add(new THREE.BoxGeometry(1.5, 0.72, 1.0), bodyMat, 0, 1.18, -0.02);

  // Roof lip
  add(new THREE.BoxGeometry(1.55, 0.08, 1.05), darkBodyMat, 0, 1.58, -0.02);

  // Windshield (front of cabin upper)
  add(new THREE.BoxGeometry(1.28, 0.52, 0.06), glassMat, 0, 1.22, -0.54);

  // Side windows
  add(new THREE.BoxGeometry(0.06, 0.42, 0.7), glassMat, 0.78, 1.22, -0.02);
  add(new THREE.BoxGeometry(0.06, 0.42, 0.7), glassMat, -0.78, 1.22, -0.02);

  // Rear cabin window (facing bed)
  add(new THREE.BoxGeometry(1.2, 0.42, 0.06), glassMat, 0, 1.22, 0.5);

  // --- Headlights ---
  add(new THREE.BoxGeometry(0.28, 0.16, 0.08), lightMat, 0.52, 0.52, -1.54);
  add(new THREE.BoxGeometry(0.28, 0.16, 0.08), lightMat, -0.52, 0.52, -1.54);

  // --- Open cargo bed ---
  // Floor
  add(new THREE.BoxGeometry(1.5, 0.1, 1.55), bedMat, 0, 0.52, 1.05);

  // Front bulkhead behind cabin
  add(new THREE.BoxGeometry(1.55, 0.55, 0.1), bodyMat, 0, 0.82, 0.3);

  // Side rails
  add(new THREE.BoxGeometry(0.1, 0.48, 1.5), bodyMat, 0.72, 0.8, 1.05);
  add(new THREE.BoxGeometry(0.1, 0.48, 1.5), bodyMat, -0.72, 0.8, 1.05);

  // Tailgate
  add(new THREE.BoxGeometry(1.5, 0.42, 0.1), darkBodyMat, 0, 0.75, 1.82);

  // Tail lights
  add(new THREE.BoxGeometry(0.22, 0.14, 0.06), tailLightMat, 0.55, 0.55, 1.88);
  add(new THREE.BoxGeometry(0.22, 0.14, 0.06), tailLightMat, -0.55, 0.55, 1.88);

  // Small spray tank on the bed (reads as farm vehicle)
  add(new THREE.CylinderGeometry(0.28, 0.32, 0.55, 8), darkBodyMat, 0, 0.9, 1.05);
  add(new THREE.CylinderGeometry(0.12, 0.12, 0.2, 6), chassisMat, 0, 1.25, 1.05);

  // --- Wheels (tire + rim) ---
  const tireGeo = new THREE.CylinderGeometry(0.38, 0.38, 0.32, 10);
  tireGeo.rotateZ(Math.PI / 2);
  const rimGeo = new THREE.CylinderGeometry(0.2, 0.2, 0.34, 8);
  rimGeo.rotateZ(Math.PI / 2);

  const wheelPositions: Array<[number, number, number]> = [
    [-0.88, 0.38, 1.15],
    [0.88, 0.38, 1.15],
    [-0.88, 0.38, -0.95],
    [0.88, 0.38, -0.95],
  ];

  wheelPositions.forEach(([x, y, z]) => {
    const tire = new THREE.Mesh(tireGeo, tireMat);
    tire.position.set(x, y, z);
    truck.add(tire);

    const rim = new THREE.Mesh(rimGeo, rimMat);
    rim.position.set(x, y, z);
    truck.add(rim);
  });

  // Wheel arches (small fender bumps)
  add(new THREE.BoxGeometry(0.2, 0.2, 0.7), bodyMat, 0.85, 0.55, -0.95);
  add(new THREE.BoxGeometry(0.2, 0.2, 0.7), bodyMat, -0.85, 0.55, -0.95);
  add(new THREE.BoxGeometry(0.2, 0.2, 0.7), bodyMat, 0.85, 0.55, 1.15);
  add(new THREE.BoxGeometry(0.2, 0.2, 0.7), bodyMat, -0.85, 0.55, 1.15);

  truck.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });

  return truck;
}
